/**
 * CalendarPicker, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/CalendarPickerComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { reservedFieldNameErrors } from '../fieldName';

export const CalendarPickerCore = {
  type: 'CalendarPicker',
  name: 'Calendar Picker',
  icon: '🗓️',
  description: 'Pick a date or date range from a full calendar.',
  category: 'input',
  maxPerScreen: null,
  getDefaultProperties() {
    return {
      name: 'calendar_picker',
      label: 'Select date',
      'end-label': '',
      mode: 'single',
      'min-date': '',
      'max-date': '',
      'unavailable-dates': [],
      'include-days': [],
      'min-days': '',
      'max-days': '',
      title: '',
      description: '',
      'helper-text': '',
      'error-message': '',
      required: false,
      enabled: true,
      visible: true,
      'init-value': '',
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      'end-label': {
        type: 'string',
        label: 'End date label (range mode)',
        required: false,
      },
      mode: {
        type: 'select',
        label: 'Mode',
        required: false,
        options: ['single', 'range'],
      },
      'min-date': {
        type: 'string',
        label: 'Min Date',
        required: false,
      },
      'max-date': {
        type: 'string',
        label: 'Max Date',
        required: false,
      },
      'unavailable-dates': {
        type: 'array',
        label: 'Unavailable Dates',
        required: false,
      },
      'include-days': {
        type: 'array',
        label: 'Include Days',
        required: false,
      },
      'min-days': {
        type: 'number',
        label: 'Min Days (range mode)',
        required: false,
      },
      'max-days': {
        type: 'number',
        label: 'Max Days (range mode)',
        required: false,
      },
      title: {
        type: 'string',
        label: 'Title (range mode)',
        required: false,
      },
      description: {
        type: 'string',
        label: 'Description (range mode)',
        required: false,
      },
      'helper-text': {
        type: 'string',
        label: 'Helper Text',
        required: false,
      },
      'error-message': {
        type: 'string',
        label: 'Error Message',
        required: false,
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      enabled: {
        type: 'boolean',
        label: 'Enabled',
        required: false,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
      'init-value': {
        type: 'string',
        label: 'Initial Value',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];
    var dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    if (properties.label && properties.label.length > 40) {
      errors.push('Label must be 40 characters or less');
    }

    if (properties.mode === 'range' && properties['end-label'] && properties['end-label'].length > 40) {
      errors.push('End date label must be 40 characters or less');
    }

    if (properties.title && properties.title.length > 80) {
      errors.push('Title must be 80 characters or less');
    }

    if (properties.description && properties.description.length > 300) {
      errors.push('Description must be 300 characters or less');
    }

    if (properties['min-date'] && !dateRegex.test(properties['min-date'])) {
      errors.push('Min date must be in YYYY-MM-DD format');
    }

    if (properties['max-date'] && !dateRegex.test(properties['max-date'])) {
      errors.push('Max date must be in YYYY-MM-DD format');
    }

    if (properties['min-date'] && properties['max-date']) {
      var minDate = new Date(properties['min-date']);
      var maxDate = new Date(properties['max-date']);
      if (minDate > maxDate) {
        errors.push('Min date must be before max date');
      }
    }

    if (properties['unavailable-dates'] && Array.isArray(properties['unavailable-dates'])) {
      properties['unavailable-dates'].forEach((date, index) => {
        if (!dateRegex.test(date)) {
          errors.push(`Unavailable date ${index + 1} must be in YYYY-MM-DD format`);
        }
      });
    }

    if (properties.mode === 'range') {
      if (properties['min-days'] && properties['max-days']) {
        var minDays = parseInt(properties['min-days'], 10);
        var maxDays = parseInt(properties['max-days'], 10);
        if (!isNaN(minDays) && !isNaN(maxDays) && minDays > maxDays) {
          errors.push('Min days cannot be greater than max days');
        }
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    // In range mode WhatsApp asks for the label, helper text, error message and required flag of
    // each end separately, as {"start-date", "end-date"}; a plain string or boolean is refused
    // ("must be of type {start-date: string, end-date: string}"). Probed 2026-10-10 during the
    // preview parity audit; until then a range calendar built here could never sync.
    var range = properties.mode === 'range';
    var pair = function (start, end) { return { 'start-date': start, 'end-date': end }; };
    var result = {
      type: 'CalendarPicker',
      name: properties.name,
      label: range ? pair(properties.label, properties['end-label'] || 'End date') : properties.label,
    };

    if (properties.mode && properties.mode !== 'single') {
      result.mode = properties.mode;
    }

    if (properties['min-date'] && properties['min-date'].trim()) {
      result['min-date'] = properties['min-date'];
    }

    if (properties['max-date'] && properties['max-date'].trim()) {
      result['max-date'] = properties['max-date'];
    }

    if (properties['unavailable-dates'] && properties['unavailable-dates'].length > 0) {
      // Filter out empty strings from the array
      var filteredDates = properties['unavailable-dates'].filter(d => d && d.trim());
      if (filteredDates.length > 0) {
        result['unavailable-dates'] = filteredDates;
      }
    }

    if (properties['include-days'] && properties['include-days'].length > 0) {
      result['include-days'] = properties['include-days'].join(',');
    }

    if (properties.mode === 'range') {
      if (properties['min-days'] !== '' && properties['min-days'] !== undefined) {
        var minDays = parseInt(properties['min-days'], 10);
        if (!isNaN(minDays)) {
          result['min-days'] = minDays;
        }
      }
      if (properties['max-days'] !== '' && properties['max-days'] !== undefined) {
        var maxDays = parseInt(properties['max-days'], 10);
        if (!isNaN(maxDays)) {
          result['max-days'] = maxDays;
        }
      }
      if (properties.title && properties.title.trim()) {
        result.title = properties.title;
      }
      if (properties.description && properties.description.trim()) {
        result.description = properties.description;
      }
    }

    if (properties['helper-text'] && properties['helper-text'].trim()) {
      result['helper-text'] = range
        ? pair(properties['helper-text'], properties['helper-text'])
        : properties['helper-text'];
    }

    if (properties['error-message'] && properties['error-message'].trim()) {
      result['error-message'] = range
        ? pair(properties['error-message'], properties['error-message'])
        : properties['error-message'];
    }

    if (properties.required) {
      result.required = range ? pair(true, true) : true;
    }

    if (properties['init-value'] && properties['init-value'].trim()) {
      result['init-value'] = properties['init-value'];
    }

    if (properties.enabled === false) {
      result.enabled = false;
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    var includeDays = whatsappComponent['include-days'] || '';
    if (typeof includeDays === 'string' && includeDays) {
      includeDays = includeDays.split(',').map(d => d.trim()).filter(Boolean);
    } else if (!Array.isArray(includeDays)) {
      includeDays = [];
    }

    // Range mode carries {"start-date", "end-date"} pairs; the builder keeps one text for both
    // except the label, whose end half has its own property.
    var startOf = function (value) {
      if (typeof value === 'object' && value !== null) return value['start-date'] || value.text || '';
      return value;
    };

    var helperText = startOf(whatsappComponent['helper-text']);
    var errorMessage = startOf(whatsappComponent['error-message']);
    var label = whatsappComponent.label;
    var endLabel = typeof label === 'object' && label !== null ? label['end-date'] || '' : '';
    var required = whatsappComponent.required;
    if (typeof required === 'object' && required !== null) {
      required = !!(required['start-date'] || required['end-date']);
    }

    return {
      name: whatsappComponent.name,
      label: startOf(label),
      'end-label': endLabel,
      mode: whatsappComponent.mode || 'single',
      'min-date': whatsappComponent['min-date'] || '',
      'max-date': whatsappComponent['max-date'] || '',
      'unavailable-dates': whatsappComponent['unavailable-dates'] || [],
      'include-days': includeDays,
      'min-days': whatsappComponent['min-days'] || '',
      'max-days': whatsappComponent['max-days'] || '',
      title: whatsappComponent.title || '',
      description: whatsappComponent.description || '',
      'helper-text': helperText || '',
      'error-message': errorMessage || '',
      required: required || false,
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
      'init-value': whatsappComponent['init-value'] || '',
    };
  },
};
